import { useNavigate, useLocation } from 'react-router-dom';
import { IPrompt, usePromptStore } from '../../../store/usePrompt';
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { formatDate, updateUrlWithParams } from '../../../util/util';
import queryString from 'query-string';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faCode,
  faUser,
  faCalendar,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';

/**
 * @author taulmax
 * @date 2024.10.14
 * @description 프롬프트 페이지
 */
const Prompt = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    prompts,
    promptsPage,
    getPrompts,
    setPromptDetail,
    downloadPromptExcelTemplate,
    uploadPromptExcel,
  } = usePromptStore();
  const [searchText, setSearchText] = useState('');
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    updateUrlWithParams({ search: searchText || undefined, page: '0' }, navigate);
  };

  const handlePromptClick = useCallback(
    (prompt: IPrompt) => {
      setPromptDetail(prompt);
      navigate(`/prompt/${prompt.id}`);
    },
    [navigate, setPromptDetail]
  );

  useEffect(() => {
    const parsedQuery = queryString.parse(location.search);
    const search = (parsedQuery.search as string) || '';
    const page = parsedQuery.page ? Number(parsedQuery.page) : 0;

    setSearchText(search);
    getPrompts(search, page);
  }, [location.search, getPrompts]);

  const onClickUploadButton = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const onChangeExcelFile = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      await uploadPromptExcel(file);
      e.target.value = '';
    },
    [uploadPromptExcel]
  );

  return (
    <div className="w-full min-h-full flex flex-col">
      <div className="flex justify-between items-center pt-4 pb-4 border-b border-gray-200 mb-4 gap-4">
        <div className="flex items-center gap-6">
          <form onSubmit={handleSearch} className="!flex !items-center !gap-3">
            <div className="!flex !items-center !gap-2">
              <div className="!relative">
                <div
                  className="!relative"
                  style={{ width: '250px', paddingRight: '40px' }}
                >
                  <input
                    type="text"
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                    placeholder="검색어를 입력해주세요"
                    className="!w-full !px-3 !py-2 !border !border-gray-300 !rounded-md !shadow-sm !text-sm !font-normal !text-gray-900 placeholder:!text-gray-400 placeholder:!text-sm placeholder:!font-normal focus:!outline-none focus:!border-blue-400 disabled:!bg-gray-50 disabled:!text-gray-500 !bg-white !transition-colors !duration-200"
                    style={{ textAlign: 'left' }}
                  />
                </div>
                <button
                  type="submit"
                  className="!absolute !right-1 !top-1/2 !-translate-y-1/2 !w-8 !h-8 !bg-blue-400 hover:!bg-blue-500 !rounded !flex !items-center !justify-center !transition-colors !border-0"
                >
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="!text-white !text-sm"
                  />
                </button>
              </div>
            </div>
          </form>
        </div>
        <div className="prompt_button_group">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx"
            style={{ display: 'none' }}
            onChange={onChangeExcelFile}
          />
          <button
            onClick={() => downloadPromptExcelTemplate()}
            className="!px-4 !py-2 !bg-green-600 hover:!bg-green-700 !text-white !font-medium !rounded-lg !transition-colors !text-sm"
          >
            양식 다운로드
          </button>
          <button
            onClick={onClickUploadButton}
            className="!px-4 !py-2 !bg-green-600 hover:!bg-green-700 !text-white !font-medium !rounded-lg !transition-colors !text-sm"
          >
            엑셀 대량 등록
          </button>
          <button
            onClick={() => navigate('/prompt/create')}
            className="!px-4 !py-2 !bg-blue-500 hover:!bg-blue-600 !text-white !font-medium !rounded-lg !transition-colors !text-sm"
          >
            프롬프트 등록
          </button>
        </div>
      </div>

      <div className="w-full pb-6 flex-1">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {prompts.map(prompt => (
            <div
              key={prompt.id}
              onClick={() => handlePromptClick(prompt)}
              onMouseEnter={() => setHoveredCard(prompt.id)}
              onMouseLeave={() => setHoveredCard(null)}
              className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer border border-gray-200 hover:border-blue-400 overflow-hidden"
            >
              <div className="p-4">
                <div className="mb-3">
                  <h3
                    className="font-semibold text-gray-900 text-sm truncate"
                    title={prompt.title}
                  >
                    {prompt.title}
                  </h3>
                  <code className="text-xs text-gray-500 font-mono bg-gray-100 px-1.5 py-0.5 rounded inline-block mt-1">
                    {prompt.promptCode}
                  </code>
                </div>

                <div className="space-y-1 text-xs text-gray-600">
                  <div className="flex items-center">
                    <FontAwesomeIcon
                      icon={faUser}
                      className="mr-1.5 text-gray-400"
                    />
                    <span className="truncate">{prompt.createdBy}</span>
                  </div>

                  <div className="flex items-center">
                    <FontAwesomeIcon
                      icon={faCalendar}
                      className="mr-1.5 text-gray-400"
                    />
                    <span>{formatDate(prompt.createdAt)['yyyy.mm.dd']}</span>
                  </div>
                </div>
              </div>

              <div
                className={`h-0.5 bg-blue-500 transition-all duration-200 ${
                  hoveredCard === prompt.id ? 'opacity-100' : 'opacity-0'
                }`}
              />
            </div>
          ))}
        </div>

        {prompts.length === 0 && (
          <div className="col-span-full text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
              <FontAwesomeIcon icon={faCode} className="text-2xl text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">
              프롬프트가 없습니다
            </h3>
            <p className="text-gray-500">새로운 프롬프트를 등록해보세요</p>
          </div>
        )}
      </div>

      {promptsPage && promptsPage.totalPages > 1 && (
        <div className="w-full mt-6">
          <div className="flex justify-center items-center space-x-1">
            <button
              onClick={() =>
                updateUrlWithParams(
                  { search: searchText || undefined, page: '0' },
                  navigate
                )
              }
              disabled={promptsPage.pageable.pageNumber === 0}
              className="px-2 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              <FontAwesomeIcon icon={faChevronLeft} className="w-3 h-3" />
              <FontAwesomeIcon icon={faChevronLeft} className="w-3 h-3 -ml-1" />
            </button>

            <button
              onClick={() =>
                updateUrlWithParams(
                  {
                    search: searchText || undefined,
                    page: Math.max(
                      0,
                      promptsPage.pageable.pageNumber - 1
                    ).toString(),
                  },
                  navigate
                )
              }
              disabled={promptsPage.pageable.pageNumber === 0}
              className="px-2 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              <FontAwesomeIcon icon={faChevronLeft} className="w-3 h-3" />
            </button>

            <div className="flex space-x-0.5">
              {(() => {
                const currentPage = promptsPage.pageable.pageNumber;
                const totalPages = promptsPage.totalPages;
                let startPage = Math.max(0, currentPage - 4);
                let endPage = Math.min(totalPages - 1, startPage + 9);

                if (endPage - startPage < 9) {
                  startPage = Math.max(0, endPage - 9);
                }

                return Array.from(
                  { length: endPage - startPage + 1 },
                  (_, i) => startPage + i
                ).map(pageNum => (
                  <button
                    key={pageNum}
                    onClick={() =>
                      updateUrlWithParams(
                        {
                          search: searchText || undefined,
                          page: pageNum.toString(),
                        },
                        navigate
                      )
                    }
                    className={`px-2.5 py-1 text-sm rounded transition-colors duration-200 ${
                      pageNum === currentPage
                        ? 'bg-blue-600 text-white'
                        : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    {pageNum + 1}
                  </button>
                ));
              })()}
            </div>

            <button
              onClick={() =>
                updateUrlWithParams(
                  {
                    search: searchText || undefined,
                    page: Math.min(
                      promptsPage.totalPages - 1,
                      promptsPage.pageable.pageNumber + 1
                    ).toString(),
                  },
                  navigate
                )
              }
              disabled={
                promptsPage.pageable.pageNumber === promptsPage.totalPages - 1
              }
              className="px-2 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              <FontAwesomeIcon icon={faChevronRight} className="w-3 h-3" />
            </button>

            <button
              onClick={() =>
                updateUrlWithParams(
                  {
                    search: searchText || undefined,
                    page: (promptsPage.totalPages - 1).toString(),
                  },
                  navigate
                )
              }
              disabled={
                promptsPage.pageable.pageNumber === promptsPage.totalPages - 1
              }
              className="px-2 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              <FontAwesomeIcon icon={faChevronRight} className="w-3 h-3" />
              <FontAwesomeIcon
                icon={faChevronRight}
                className="w-3 h-3 -ml-1"
              />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Prompt;
